/*
 * Copyright 2026 Sony Corporation
 */

import type { z } from 'zod';

import {
  DeviceResponseSchema,
  DevicesResponseSchema,
  DeviceStateResponseSchema,
  ErrorResponseSchema,
  GatewaysResponseSchema,
  WorldResponseSchema,
  WorldsResponseSchema,
  type Device,
  type DeviceStateResponse,
  type CreateCaptureRequest,
  type CreateDeviceRequest,
  type CreateGatewayRequest,
  type CreateWorldRequest,
  type GatewaySummary,
  type Role,
  type UpdateDeviceRequest,
  type UpdateWorldRequest,
  type World,
  type WriteStateRequest,
} from '@ips-device-verse/twin-service/schema';
import type {
  CreateSceneRequest,
  PublishSceneRequest,
  PutWorldSceneRequest,
  Scene,
  SceneLoadResponse,
  SceneUploadUrlResponse,
  UpdateSceneRequest,
} from '@ips-device-verse/twin-service/schema/scenes';
import {
  SceneDocumentResponseSchema,
  SceneLoadResponseSchema,
  SceneResponseSchema,
  ScenesResponseSchema,
  SceneUploadUrlResponseSchema,
} from '@ips-device-verse/twin-service/schema/scenes';
import {
  SketchfabSearchResponseSchema,
  type SketchfabSearchQuery,
  type SketchfabSearchResponse,
} from '@ips-device-verse/twin-service/schema/assets';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface GatewayBundle {
  blob: Blob;
  filename: string;
}

export interface ApiClient {
  listWorlds: () => Promise<World[]>;
  createWorld: (body: CreateWorldRequest) => Promise<World>;
  getWorld: (worldId: string) => Promise<{ world: World; role?: Role }>;
  updateWorld: (worldId: string, body: UpdateWorldRequest) => Promise<World>;
  deleteWorld: (worldId: string) => Promise<void>;
  listDevices: (worldId: string) => Promise<Device[]>;
  createDevice: (worldId: string, body: CreateDeviceRequest) => Promise<Device>;
  updateDevice: (worldId: string, deviceId: string, body: UpdateDeviceRequest) => Promise<Device>;
  deleteDevice: (worldId: string, deviceId: string) => Promise<void>;
  getDeviceState: (worldId: string, deviceId: string) => Promise<DeviceStateResponse>;
  writeDeviceState: (worldId: string, deviceId: string, body: WriteStateRequest) => Promise<void>;
  /** Answers a virtual camera's shutter release with the still rendered for it. */
  createCapture: (worldId: string, deviceId: string, body: CreateCaptureRequest) => Promise<void>;
  setWorldScene: (worldId: string, body: PutWorldSceneRequest) => Promise<World>;
  resetWorldScene: (worldId: string) => Promise<World>;
  listScenes: (worldId: string) => Promise<Scene[]>;
  createScene: (worldId: string, body: CreateSceneRequest) => Promise<Scene>;
  getScene: (worldId: string, sceneId: string) => Promise<Scene>;
  updateScene: (worldId: string, sceneId: string, body: UpdateSceneRequest) => Promise<Scene>;
  deleteScene: (worldId: string, sceneId: string) => Promise<void>;
  /** Undefined for a draft nobody has saved yet, which is not an error. */
  getSceneDocument: (worldId: string, sceneId: string) => Promise<unknown>;
  putSceneDocument: (worldId: string, sceneId: string, document: unknown) => Promise<void>;
  createSceneUploadUrl: (worldId: string, sceneId: string, bytes: number) => Promise<SceneUploadUrlResponse>;
  publishScene: (worldId: string, sceneId: string, body: PublishSceneRequest) => Promise<Scene>;
  getSceneLoad: (worldId: string, sceneId: string) => Promise<SceneLoadResponse>;
  listGateways: (worldId: string) => Promise<GatewaySummary[]>;
  createGatewayBundle: (worldId: string, body: CreateGatewayRequest) => Promise<GatewayBundle>;
  /** CC0 models only; the service, not this client, decides that (devplan §5.6). */
  searchSketchfab: (query: SketchfabSearchQuery, signal?: AbortSignal) => Promise<SketchfabSearchResponse>;
}

export interface ApiClientOptions {
  baseUrl: string;
  getIdToken: () => Promise<string | undefined>;
}

const FILENAME_PATTERN = /filename="?([^";]+)"?/;

const scenesPath = (worldId: string): string => `/worlds/${encodeURIComponent(worldId)}/scenes`;
const scenePath = (worldId: string, sceneId: string): string => `${scenesPath(worldId)}/${encodeURIComponent(sceneId)}`;

/** Every response is parsed with the service's own schema, so a contract change surfaces here rather than three components deep. */
const parseWith = async <T extends z.ZodType>(response: Response, schema: T): Promise<z.infer<T>> => {
  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    throw new ApiError(response.status, 'The server returned a response this client does not understand.');
  }
  return parsed.data;
};

export const createApiClient = ({ baseUrl, getIdToken }: ApiClientOptions): ApiClient => {
  const root = `${baseUrl.replace(/\/+$/, '')}/v1`;

  const request = async (path: string, init: RequestInit = {}): Promise<Response> => {
    const token = await getIdToken();
    if (!token) {
      throw new ApiError(401, 'Your session has expired. Sign in again.');
    }

    const response = await fetch(`${root}${path}`, {
      ...init,
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
        Authorization: token,
      },
    });

    if (!response.ok) {
      // The error body is the service's own shape; anything else (a gateway
      // error page, say) must not be shown to the user verbatim.
      const body = await response.json().catch(() => undefined);
      const parsed = ErrorResponseSchema.safeParse(body);
      throw new ApiError(response.status, parsed.success ? parsed.data.message : response.statusText);
    }

    return response;
  };

  const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

  return {
    listWorlds: async () => (await parseWith(await request('/worlds'), WorldsResponseSchema)).worlds,

    createWorld: async (body) =>
      (await parseWith(await request('/worlds', { method: 'POST', ...json(body) }), WorldResponseSchema)).world,

    getWorld: async (worldId) => {
      const { world, role } = await parseWith(await request(`/worlds/${worldId}`), WorldResponseSchema);
      return { world, role };
    },

    updateWorld: async (worldId, body) =>
      (await parseWith(await request(`/worlds/${worldId}`, { method: 'PATCH', ...json(body) }), WorldResponseSchema))
        .world,

    deleteWorld: async (worldId) => {
      await request(`/worlds/${worldId}`, { method: 'DELETE' });
    },

    listDevices: async (worldId) =>
      (await parseWith(await request(`/worlds/${worldId}/devices`), DevicesResponseSchema)).devices,

    createDevice: async (worldId, body) =>
      (
        await parseWith(
          await request(`/worlds/${worldId}/devices`, { method: 'POST', ...json(body) }),
          DeviceResponseSchema
        )
      ).device,

    updateDevice: async (worldId, deviceId, body) =>
      (
        await parseWith(
          await request(`/worlds/${worldId}/devices/${deviceId}`, { method: 'PATCH', ...json(body) }),
          DeviceResponseSchema
        )
      ).device,

    deleteDevice: async (worldId, deviceId) => {
      await request(`/worlds/${worldId}/devices/${deviceId}`, { method: 'DELETE' });
    },

    getDeviceState: async (worldId, deviceId) =>
      parseWith(await request(`/worlds/${worldId}/devices/${deviceId}/state`), DeviceStateResponseSchema),

    writeDeviceState: async (worldId, deviceId, body) => {
      await request(`/worlds/${worldId}/devices/${deviceId}/state`, { method: 'PUT', ...json(body) });
    },

    createCapture: async (worldId, deviceId, body) => {
      await request(`/worlds/${worldId}/devices/${deviceId}/captures`, { method: 'POST', ...json(body) });
    },

    setWorldScene: async (worldId, body) =>
      (
        await parseWith(
          await request(`/worlds/${worldId}/scene`, { method: 'PUT', ...json(body) }),
          WorldResponseSchema
        )
      ).world,

    resetWorldScene: async (worldId) =>
      (await parseWith(await request(`/worlds/${worldId}/scene`, { method: 'DELETE' }), WorldResponseSchema)).world,

    listScenes: async (worldId) => (await parseWith(await request(scenesPath(worldId)), ScenesResponseSchema)).scenes,

    createScene: async (worldId, body) =>
      (await parseWith(await request(scenesPath(worldId), { method: 'POST', ...json(body) }), SceneResponseSchema))
        .scene,

    getScene: async (worldId, sceneId) =>
      (await parseWith(await request(scenePath(worldId, sceneId)), SceneResponseSchema)).scene,

    updateScene: async (worldId, sceneId, body) =>
      (
        await parseWith(
          await request(scenePath(worldId, sceneId), { method: 'PATCH', ...json(body) }),
          SceneResponseSchema
        )
      ).scene,

    deleteScene: async (worldId, sceneId) => {
      await request(scenePath(worldId, sceneId), { method: 'DELETE' });
    },

    getSceneDocument: async (worldId, sceneId) => {
      try {
        return (await parseWith(await request(`${scenePath(worldId, sceneId)}/document`), SceneDocumentResponseSchema))
          .document;
      } catch (error) {
        // A draft opened for the first time has no document, which the editor
        // answers with an empty one rather than with a failure.
        if (error instanceof ApiError && error.status === 404) {
          return undefined;
        }
        throw error;
      }
    },

    putSceneDocument: async (worldId, sceneId, document) => {
      await request(`${scenePath(worldId, sceneId)}/document`, { method: 'PUT', ...json(document) });
    },

    createSceneUploadUrl: async (worldId, sceneId, bytes) =>
      parseWith(
        await request(`${scenePath(worldId, sceneId)}/upload-url`, { method: 'POST', ...json({ bytes }) }),
        SceneUploadUrlResponseSchema
      ),

    publishScene: async (worldId, sceneId, body) =>
      (
        await parseWith(
          await request(`${scenePath(worldId, sceneId)}/publish`, { method: 'POST', ...json(body) }),
          SceneResponseSchema
        )
      ).scene,

    getSceneLoad: async (worldId, sceneId) =>
      parseWith(await request(`${scenePath(worldId, sceneId)}/load`), SceneLoadResponseSchema),

    listGateways: async (worldId) =>
      (await parseWith(await request(`/worlds/${worldId}/gateways`), GatewaysResponseSchema)).gateways,
    createGatewayBundle: async (worldId, body) => {
      const response = await request(`/worlds/${worldId}/gateways`, {
        method: 'POST',
        headers: { Accept: 'application/zip' },
        ...json(body),
      });
      const disposition = response.headers.get('Content-Disposition') ?? '';
      return {
        blob: await response.blob(),
        filename: FILENAME_PATTERN.exec(disposition)?.[1] ?? `gateway-${worldId}.zip`,
      };
    },

    searchSketchfab: async (query, signal) => {
      const params = new URLSearchParams();
      if (query.q) {
        params.set('q', query.q);
      }
      if (query.cursor) {
        params.set('cursor', query.cursor);
      }
      const suffix = params.toString();
      return parseWith(
        await request(`/assets/sketchfab${suffix ? `?${suffix}` : ''}`, { signal }),
        SketchfabSearchResponseSchema
      );
    },
  };
};
